import type { EClickRankType } from '@gem-sdk/heatmap-react';

import {
  useHeatmapClickRankConfig,
  useHeatmapDisableAction,
  useHeatmapMetaContext,
} from '~/modules/heatmap';
import { Box, Button, Icon, InlineStack, TextField } from '@shopify/polaris';
import { useHeatmapSettingContext } from '@gem-sdk/heatmap-react';
import { SearchIcon, XIcon } from '@shopify/polaris-icons';
import { GInfoPanelPopover } from '~/presentation/common';
import { useState, useCallback, useEffect } from 'react';
import { GBlockCenter } from '~/presentation/ui';
import { useTranslation } from 'react-i18next';
import { SPACING_INSET } from '../Sidebar';

export const SelectClickRank = () => {
  const searchQuery = useHeatmapMetaContext((s) => s.rankQuery);
  const setRankQuery = useHeatmapMetaContext((s) => s.setRankQuery);
  const rankedBy = useHeatmapSettingContext((state) => state.rankedBy);
  const setRankedBy = useHeatmapSettingContext((state) => state.setRankedBy);

  const { t } = useTranslation();
  const { selectItems } = useHeatmapClickRankConfig();
  const { isDisableAction } = useHeatmapDisableAction();

  const [isSearchActive, setIsSearchActive] = useState(false);

  const handleRankedByChange = useCallback((id: string) => setRankedBy(id as EClickRankType), []);

  const handleSearchClick = useCallback(() => {
    setIsSearchActive(true);
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      setRankQuery(value);
    },
    [setRankQuery],
  );

  const handleCloseSearch = useCallback(() => {
    setIsSearchActive(false);
    setRankQuery('');
  }, [setRankQuery]);

  useEffect(() => {
    return () => {
      handleCloseSearch();
    };
  }, [handleCloseSearch]);

  if (isSearchActive) {
    return (
      <InlineStack gap="200" align="space-between" blockAlign="center" wrap={false}>
        <div style={{ flex: 1 }}>
          <TextField
            focused
            labelHidden
            label="Search element"
            placeholder={t('Search element')}
            autoComplete="off"
            value={searchQuery}
            prefix={<Icon source={SearchIcon} tone="subdued" />}
            onChange={handleSearchChange}
          />
        </div>

        <GBlockCenter width="32px" height="32px">
          <Button size="large" variant="tertiary" icon={XIcon} onClick={handleCloseSearch}></Button>
        </GBlockCenter>
      </InlineStack>
    );
  }

  return (
    <InlineStack gap={SPACING_INSET} blockAlign="center" align="space-between" wrap={false}>
      <Box minWidth="66px">
        <span className="Polaris-Text--root Polaris-Text--bodyMd Polaris-Text--medium">
          {t('Ranked by')}
        </span>
      </Box>

      <InlineStack gap="200" blockAlign="center" wrap={false}>
        <Box maxWidth="100%">
          <GInfoPanelPopover
            disabled={isDisableAction}
            items={selectItems}
            selectedId={rankedBy}
            onSelect={handleRankedByChange}
            minWidth="236px"
            columns="1fr"
            rightHidden
            defaultInfo={{
              title: t('Select ranking metric'),
              description: t('Choose how to rank the elements on your page'),
            }}
          />
        </Box>

        <GBlockCenter>
          <Button
            size="large"
            variant="secondary"
            disabled={isDisableAction}
            icon={SearchIcon}
            onClick={handleSearchClick}
          ></Button>
        </GBlockCenter>
      </InlineStack>
    </InlineStack>
  );
};
